import type { OpeningLayoutBounds } from "building-types/framing/framingLayoutTypes";
import type { FramingConfig } from "building-types/framing/framingMetaDataTypes";
import type {
  BandFramingOptions,
  BandJackKingResolution,
  OpeningFramingConfigResolver,
} from "building-types/framing/wallFramingOpeningConfigTypes";
import type {
  WallFramingInput,
  WallOpeningBoundary,
  WallOpeningFramingConfigGroup,
} from "building-types/framing/wallFramingTypes";
import { bandKingJackPerpPositions } from "./wallFramingOpenings";

export const DEFAULT_BAND_FRAMING_OPTIONS: BandFramingOptions = {
  mergeAlignedOpeningJackKingCounts: false,
};

export function bandFramingOptionsFromInput(
  input: Pick<WallFramingInput, "mergeAlignedOpeningJackKingCounts">,
): BandFramingOptions {
  return {
    mergeAlignedOpeningJackKingCounts: input.mergeAlignedOpeningJackKingCounts === true,
  };
}

export function collectBandOpeningFramingConfigs(
  openings: readonly { readonly openingIndex: number }[],
  resolveOpeningConfig: OpeningFramingConfigResolver,
): WallOpeningFramingConfigGroup[] {
  const configs: WallOpeningFramingConfigGroup[] = [];
  for (const opening of openings) {
    const config = resolveOpeningConfig(opening.openingIndex);
    if (config) {
      configs.push(config);
    }
  }
  return configs;
}

export function createBandJackKingResolution(
  openings: readonly { readonly openingIndex: number }[],
  resolveOpeningConfig: OpeningFramingConfigResolver,
  options: BandFramingOptions,
): BandJackKingResolution {
  const enabled = options.mergeAlignedOpeningJackKingCounts;
  const merged =
    openings.length > 1
      ? mergeBandJackKingFramingConfig(collectBandOpeningFramingConfigs(openings, resolveOpeningConfig))
      : undefined;
  return { enabled, merged };
}

/**
 * Resolved per-opening config: wall/opening override, then band max jack/king counts when the band
 * has multiple vertically stacked / perp-overlapping openings.
 */
export function resolveOpeningFramingConfigForBand(
  openingIndex: number,
  resolveOpeningConfig: OpeningFramingConfigResolver,
  jackKingResolution: BandJackKingResolution,
): WallOpeningFramingConfigGroup | undefined {
  const config = resolveOpeningConfig(openingIndex);
  if (!config) {
    return undefined;
  }
  if (jackKingResolution.merged === undefined) {
    return config;
  }
  return withBandJackKingCounts(config, jackKingResolution.merged);
}

function framingMemberCount(config: FramingConfig | undefined, defaultCount: number = 1): number {
  if (config?.disabled) {
    return 0;
  }
  return Math.max(1, config?.count ?? defaultCount);
}

/**
 * For a vertically aligned / perp-overlapping opening band, use the maximum jack and king counts
 * from all openings in the band so shared king studs and jack layout stay consistent.
 */
export function mergeBandJackKingFramingConfig(
  configs: readonly WallOpeningFramingConfigGroup[],
): WallOpeningFramingConfigGroup | undefined {
  if (configs.length === 0) {
    return undefined;
  }
  const base = configs[0];
  let maxJack = 0;
  let maxKing = 0;
  for (const config of configs) {
    maxJack = Math.max(maxJack, framingMemberCount(config.jackStudConfig));
    maxKing = Math.max(maxKing, framingMemberCount(config.kingStudConfig));
  }
  return {
    ...base,
    jackStudConfig: {
      ...base.jackStudConfig,
      count: maxJack > 0 ? maxJack : 1,
      disabled: maxJack <= 0,
    },
    kingStudConfig: {
      ...base.kingStudConfig,
      count: maxKing > 0 ? maxKing : 1,
      disabled: maxKing <= 0,
    },
  };
}

/**
 * King and jack centerlines across the band RO for bottom / inter-opening cripple filtering.
 * Uses band max jack/king counts for multi-opening bands.
 */
export function bandBottomCrippleObstructingStudPerps(
  band: {
    readonly openings: readonly { readonly openingIndex: number }[];
    readonly combinedRoughOpeningBounds: OpeningLayoutBounds;
  },
  jackKingResolution: BandJackKingResolution,
  studThickness: number,
): readonly number[] | undefined {
  if (band.openings.length <= 1) {
    return undefined;
  }
  const config = jackKingResolution.merged;
  if (!config) {
    return undefined;
  }
  return bandKingJackPerpPositions(band.combinedRoughOpeningBounds, config, studThickness);
}

/** Apply band-wide jack/king counts while keeping other members per opening. */
export function withBandJackKingCounts(
  config: WallOpeningFramingConfigGroup,
  bandJackKing: WallOpeningFramingConfigGroup,
): WallOpeningFramingConfigGroup {
  return {
    ...config,
    jackStudConfig: bandJackKing.jackStudConfig,
    kingStudConfig: bandJackKing.kingStudConfig,
  };
}

/** Resolver that returns the same config for every opening index. */
export function constantOpeningFramingConfigResolver(
  config: WallOpeningFramingConfigGroup,
): OpeningFramingConfigResolver {
  return () => config;
}

export function resolveOpeningFramingConfig(
  wallDefault: WallOpeningFramingConfigGroup | undefined,
  opening: WallOpeningBoundary | undefined,
): WallOpeningFramingConfigGroup | undefined {
  return opening?.openingFramingConfigGroup ?? wallDefault;
}

export function openingFramingConfigResolverFromInput(
  openings: readonly WallOpeningBoundary[] | undefined,
  wallDefault: WallOpeningFramingConfigGroup | undefined,
): OpeningFramingConfigResolver {
  return (openingIndex: number) => resolveOpeningFramingConfig(wallDefault, openings?.[openingIndex]);
}
